/**
 * @file storageService.ts
 * @fileoverview Firebase Cloud Storage service for file uploads, downloads, and management.
 */

import {
  ref,
  uploadBytes,
  getDownloadURL,
  getMetadata,
  deleteObject,
  listAll,
  uploadBytesResumable,
  SettableMetadata,
  StorageReference,
} from "firebase/storage";
import { storage } from "@idk/config";

/**
 * Defines the configuration for a file upload operation.
 */
export interface UploadConfig {
  path: string;
  file: File;
  metadata?: SettableMetadata;
}

/**
 * Represents a file stored in Firebase Storage, typically for use in a media library.
 */
export interface StorageFile {
  url: string;
  path: string;
  name: string;
}

/**
 * Extracts the storage path from a Firebase Storage download URL.
 * useful for converting a public URL back to a storage reference.
 */
export function getStoragePathFromUrl(url: string): string | null {
  if (!url || !url.includes("firebasestorage.googleapis.com")) return null;

  try {
    const decodedUrl = decodeURIComponent(url);
    const pathMatch = decodedUrl.match(/\/o\/(.+?)\?/);
    return pathMatch ? pathMatch[1] : null;
  } catch {
    return null;
  }
}

/** One stored file with the details a file list shows. */
export interface StorageFileDetails extends StorageFile {
  size: number;
  contentType: string;
  /** Upload time, ms since the epoch. */
  updated: number;
  customMetadata: Record<string, string>;
}

export const StorageService = {
  /**
   * Uploads a file to an exact path with no client-side size cap (the Storage
   * rules for that path set the limit). Returns the stored file's details.
   */
  putFile: async (config: UploadConfig): Promise<StorageFileDetails> => {
    const storageRef = ref(storage, config.path);
    const snapshot = await uploadBytes(storageRef, config.file, config.metadata);
    const [url, meta] = await Promise.all([
      getDownloadURL(snapshot.ref),
      getMetadata(snapshot.ref),
    ]);
    return {
      url,
      path: snapshot.ref.fullPath,
      name: snapshot.ref.name,
      size: meta.size,
      contentType: meta.contentType ?? "",
      updated: Date.parse(meta.updated),
      customMetadata: meta.customMetadata ?? {},
    };
  },

  /** Lists the files directly inside a folder with size, type and upload time. */
  /** A download link for one stored file (storage.rules decide who may). */
  urlFor: (path: string): Promise<string> => getDownloadURL(ref(storage, path)),

  listDetailed: async (folderPath: string): Promise<StorageFileDetails[]> => {
    const res = await listAll(ref(storage, folderPath));
    return Promise.all(
      res.items.map(async (itemRef: StorageReference) => {
        const [url, meta] = await Promise.all([getDownloadURL(itemRef), getMetadata(itemRef)]);
        return {
          url,
          path: itemRef.fullPath,
          name: itemRef.name,
          size: meta.size,
          contentType: meta.contentType ?? "",
          updated: Date.parse(meta.updated),
          customMetadata: meta.customMetadata ?? {},
        };
      }),
    );
  },

  /**
   * Uploads a file to Firebase Storage. Ideal for smaller files where progress tracking is not required.
   * @param config The upload configuration, including path, file, and optional metadata.
   * @returns A promise that resolves with the public download URL of the uploaded file.
   * @throws An error if the file size exceeds 5MB.
   */
  uploadFile: async (config: UploadConfig): Promise<string> => {
    const { path, file, metadata } = config;

    // Basic validation to prevent excessively large uploads.
    if (file.size > 5 * 1024 * 1024) {
      throw new Error("File size exceeds 5MB limit.");
    }

    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file, metadata);
    return await getDownloadURL(snapshot.ref);
  },

  /**
   * Uploads a file with progress tracking, suitable for larger files or interactive UI.
   * @param config The upload configuration.
   * @param onProgress Callback function that receives the upload progress percentage.
   * @param onError Callback function for handling upload errors.
   * @param onComplete Callback function called with the download URL upon successful completion.
   * @returns A function that can be called to cancel the upload task.
   */
  uploadWithProgress: (
    config: UploadConfig,
    onProgress: (progress: number) => void,
    onError: (error: Error) => void,
    onComplete: (url: string) => void,
  ): (() => void) => {
    const { path, file, metadata } = config;
    const storageRef = ref(storage, path);

    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress(progress);
      },
      (error) => {
        console.error("Upload failed:", error);
        onError(error);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        onComplete(downloadURL);
      },
    );

    return () => uploadTask.cancel();
  },

  /**
   * Lists all files within a specified folder path.
   * @param folderPath The path of the folder to list (e.g., 'images/gallery/').
   * @returns A promise that resolves to an array of `StorageFile` objects.
   * @description For performance in large media libraries, it's often better to store file metadata in Firestore rather than relying solely on `listAll`.
   */
  listFiles: async (folderPath: string): Promise<StorageFile[]> => {
    const listRef = ref(storage, folderPath);
    const res = await listAll(listRef);

    // Asynchronously retrieve the download URL for each file reference.
    const files = await Promise.all(
      res.items.map(async (itemRef: StorageReference) => {
        const url = await getDownloadURL(itemRef);
        return {
          url,
          path: itemRef.fullPath,
          name: itemRef.name,
        };
      }),
    );

    return files;
  },

  /**
   * Retrieves the public download URL for a file at a given path.
   * @param path The full path to the file in Firebase Storage.
   * @returns A promise that resolves with the file's download URL.
   */
  getUrl: async (path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  },

  /**
   * Deletes a single file from Firebase Storage using its path.
   * @param path The full path to the file to be deleted.
   */
  deleteFile: async (path: string) => {
    const storageRef = ref(storage, path);
    return await deleteObject(storageRef);
  },

  /**
   * Deletes a single file from Firebase Storage using its public URL.
   * @param url The public download URL of the file.
   * @returns boolean indicating success or failure.
   */
  deleteByUrl: async (url: string): Promise<boolean> => {
    const path = getStoragePathFromUrl(url);
    if (!path) return false;

    try {
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      return true;
    } catch (error: unknown) {
      // Treat already-missing objects as success (no-op) to avoid log spam
      // from stale/backfilled URLs that never had a real storage object.
      const code = (error as { code?: string })?.code;
      if (code === "storage/object-not-found") {
        return true;
      }
      console.error("Failed to delete storage file:", error);
      return false;
    }
  },

  /**
   * Deletes multiple files from Firebase Storage in a single batch operation.
   * @param paths An array of full paths to the files to be deleted.
   */
  deleteMultiple: async (paths: string[]) => {
    const deletePromises = paths.map((path) => {
      const storageRef = ref(storage, path);
      return deleteObject(storageRef);
    });
    return await Promise.all(deletePromises);
  },

  /**
   * Compares old and new image arrays, and deletes the ones that were removed.
   * @param oldUrls The array of image URLs before the update.
   * @param newUrls The array of image URLs after the update.
   */
  cleanupRemovedFiles: async (
    oldUrls: string[],
    newUrls: string[],
  ): Promise<void> => {
    const removedUrls = oldUrls.filter((url) => !newUrls.includes(url));

    // We use StorageService.deleteByUrl specifically here
    await Promise.all(
      removedUrls.map((url) => StorageService.deleteByUrl(url)),
    );
  },
};
